// SSE streaming client for POST /api/chat.
//
// Django proxies FastAPI's chat SSE stream as-is. Frames are separated
// by a blank line (\n\n). Each frame contains one or more "data:" lines
// whose concatenated payload is either "[DONE]" or a JSON event object
// carrying a `type` discriminator.
//
// Port of the parsing loop in obsidian-plugin/src/api/local.ts, but
// expressed as an async generator instead of a callback-heavy method.

import { apiFetch, getCsrfToken } from './api';
import { getClientId } from './clientId';
import type {
  SourceDoc,
  PipelineStep,
  PlanningEvent,
  GraphTraversalEvent,
  ProfileEvent,
  DetectedIOC,
  StepDetail,
} from '../types';

/** Optional timing fields of a progress event (see StepDetail). */
export type StepTimingTags = Omit<StepDetail, 'step' | 'message' | 'count' | 'ts'>;

export type ChatEvent =
  | { type: 'turn_id'; turn_id: string; chat_id: string; seq: number }
  | { type: 'routing'; mode: string; confidence: number; reasoning: string }
  | { type: 'detected_iocs'; source: 'query' | 'response'; iocs: DetectedIOC[] }
  | {
      type: 'sources';
      sources: SourceDoc[];
      min_score?: number | null;
      cut_score?: number | null;
      cutoff_dropped?: number;
      excluded_sources?: SourceDoc[];
    }
  | ({ type: 'progress'; step: PipelineStep; message: string; count?: number; ts?: number } & StepTimingTags)
  | { type: 'planning_token'; content: string }
  | { type: 'token'; content: string }
  | { type: 'error'; content: string }
  | { type: 'queued'; position: number; ahead: number }
  | { type: 'cancelled'; content?: string }
  | ({ type: 'planning' } & PlanningEvent)
  | ({ type: 'profile' } & ProfileEvent)
  | ({ type: 'graph_traversal' } & GraphTraversalEvent);

/** Every buffered data frame of a server-side chat run carries a 1-based,
 *  monotonic `run_seq`; re-attach resumes after the last one seen. */
export type ChatStreamEvent = (ChatEvent & { run_seq?: number }) | { type: 'done' };

export interface StreamChatBody {
  message: string;
  history: { role: 'user' | 'assistant'; content: string }[];
  chat_id?: string;
  is_temporary?: boolean;
  mode?: string;
  top_k?: number;
  retrieval_depth?: string;
  ioc_pivot?: boolean;
  web_search?: boolean;
  neighbor_expansion?: boolean;
  full_doc_context?: boolean;
  custom_top_sources?: number;
  custom_fusion_cap?: number;
  custom_max_chunks_per_doc?: number;
  [key: string]: unknown;
}

export async function* streamChat(
  body: StreamChatBody,
  signal?: AbortSignal,
): AsyncGenerator<ChatStreamEvent, void, void> {
  const csrf = await getCsrfToken();
  const resp = await fetch('/api/chat', {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      'X-CSRFToken': csrf,
      'X-Client-Id': getClientId(),
    },
    body: JSON.stringify(body),
    signal,
  });

  if (!resp.ok || !resp.body) {
    const errText = await resp.text().catch(() => resp.statusText);
    throw new Error(`chat request failed: ${resp.status} ${errText || ''}`.trim());
  }
  yield* readChatSse(resp);
}

/** The run is unknown to the server: finished and evicted, someone else's,
 *  or lost to a server restart. Its turn (if any) is in the chat detail. */
export class RunGoneError extends Error {
  constructor(runId: string) {
    super(`chat run ${runId} is no longer available`);
    this.name = 'RunGoneError';
  }
}

/** Re-attach to a server-side chat run: replays frames after `after`
 *  (a run_seq), then tails live ones until [DONE]. */
export async function* attachChatRun(
  runId: string,
  after: number,
  signal?: AbortSignal,
): AsyncGenerator<ChatStreamEvent, void, void> {
  const qs = after > 0 ? `?after=${after}` : '';
  const resp = await fetch(`/api/chat/runs/${encodeURIComponent(runId)}/events${qs}`, {
    method: 'GET',
    credentials: 'same-origin',
    headers: { Accept: 'text/event-stream', 'X-Client-Id': getClientId() },
    signal,
  });
  if (resp.status === 404) throw new RunGoneError(runId);
  if (!resp.ok || !resp.body) {
    const errText = await resp.text().catch(() => resp.statusText);
    throw new Error(`re-attach failed: ${resp.status} ${errText || ''}`.trim());
  }
  yield* readChatSse(resp);
}

export interface ChatRunInfo {
  run_id: string;
  turn_id: string;
  chat_id: string | null;
  status: string;
  query: string;
  last_seq: number;
  started_at: number;
}

/** The caller's still-running chat turns (any tab, any page load). */
export function listChatRuns(): Promise<ChatRunInfo[]> {
  return apiFetch<ChatRunInfo[]>('/api/chat/runs');
}

/** Stop a run server-side. Closing the stream only detaches from it. */
export function cancelChatRun(runId: string): Promise<{ cancelled: boolean; status: string }> {
  return apiFetch(`/api/chat/runs/${encodeURIComponent(runId)}/cancel`, { method: 'POST' });
}

async function* readChatSse(resp: Response): AsyncGenerator<ChatStreamEvent, void, void> {
  const reader = resp.body!.getReader();
  const decoder = new TextDecoder();
  let buf = '';

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });

      // SSE frames are separated by blank lines. Support both LF and CRLF.
      let sepIdx: number;
      while (true) {
        const lfIdx = buf.indexOf('\n\n');
        const crlfIdx = buf.indexOf('\r\n\r\n');
        if (lfIdx === -1 && crlfIdx === -1) {
          sepIdx = -1;
          break;
        }
        let sepLen: number;
        if (lfIdx === -1) {
          sepIdx = crlfIdx;
          sepLen = 4;
        } else if (crlfIdx === -1) {
          sepIdx = lfIdx;
          sepLen = 2;
        } else if (crlfIdx < lfIdx) {
          sepIdx = crlfIdx;
          sepLen = 4;
        } else {
          sepIdx = lfIdx;
          sepLen = 2;
        }

        const frame = buf.slice(0, sepIdx);
        buf = buf.slice(sepIdx + sepLen);

        const dataLines = frame
          .split(/\r?\n/)
          .filter((l) => l.startsWith('data:'))
          .map((l) => l.slice(5).replace(/^ /, '')); // strip single leading space per SSE spec

        if (dataLines.length === 0) continue;
        const payload = dataLines.join('\n').trim();
        if (!payload) continue;
        if (payload === '[DONE]') {
          yield { type: 'done' };
          return;
        }

        try {
          const parsed = JSON.parse(payload) as ChatEvent & { run_seq?: number };
          yield parsed;
        } catch (e) {
          // Malformed SSE frame — log and continue. Matches the
          // Obsidian client's forgiving behaviour.
          // eslint-disable-next-line no-console
          console.warn('malformed SSE frame', payload, e);
        }
      }
    }
  } finally {
    try {
      await reader.cancel();
    } catch {
      // Reader might already be closed; ignore.
    }
  }
}
